import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmComponent} from '@components/popup/confirm/confirm.component';
import {Favorite} from '@models/favorite';
import {AdUserFavorite} from '@models/ad';
import {AdService} from '@services/ad.service';
import {AlertService} from '@services/alert.service';
import {TrackingService} from '@services/tracking.service';

@Component({
    selector: 'app-publish',
    templateUrl: './publish.component.html',
    styleUrls: ['./publish.component.scss']
})
export class PublishComponent {

    favorite: Favorite;
    isPublished = false;
    // Annonce déjà diffusée sur un portail (ex. Leboncoin) : elle ne peut pas être déclarée off-market
    offMarketLocked = false;
    url = '';
    offMarket = false;
    offMarketEndDateObj: Date = null; // valeur du datepicker (affichée en JJ/MM/AAAA)
    minDateObj = new Date();
    minDate = new Date().toISOString().substring(0, 10);
    loading = false;
    error = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private adService: AdService,
        private alertService: AlertService,
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<PublishComponent>,
        public trackingService: TrackingService) {

        this.favorite = data.favorite;
        this.url = this.adService.getPublishedUrl(this.favorite);
        this.isPublished = this.adService.isPublished(this.favorite);
        const published = this.isPublished ? this.favorite.ad.user_favorite : null;
        const urls = this.favorite.ad && Array.isArray(this.favorite.ad.urls) ? this.favorite.ad.urls : [];
        this.offMarketLocked = !!this.favorite.ad_id && urls.some(u => !!u.provider && u.provider !== 'user');
        this.offMarket = !this.offMarketLocked && !!(published && published.offmarket);
        const endDate = published ? this.toInputDate(published.offmarket_end_date) : '';
        this.offMarketEndDateObj = endDate ? new Date(+endDate.substring(0, 4), +endDate.substring(5, 7) - 1, +endDate.substring(8, 10)) : null;
        this.minDateObj.setHours(0, 0, 0, 0);
    }

    // Date choisie -> YYYY-MM-DD (date locale) pour l'API
    get offMarketEndDate(): string {
        const d = this.offMarketEndDateObj;
        if (!d || isNaN(d.getTime())) {
            return '';
        }
        const pad = (n: number) => ('0' + n).slice(-2);
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }

    // Date API (ISO avec fuseau, ou déjà YYYY-MM-DD) -> valeur d'un <input type="date">, en date locale
    private toInputDate(value: string): string {
        if (!value) {
            return '';
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
        }
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            return '';
        }
        const pad = (n: number) => ('0' + n).slice(-2);
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }

    onNoClick(): void {
        this.dialogRef.close(false);
    }

    // Dépublication (DELETE app/favorite/{id}/publish), après confirmation
    unpublish(): void {
        if (this.loading) {
            return;
        }
        const confirmRef = this.dialog.open(ConfirmComponent, {
            width: '420px',
            data: {message: 'Êtes-vous sûr de vouloir retirer ce bien du moteur de recherche LyBox ?'},
        });
        confirmRef.afterClosed().subscribe(confirmed => {
            if (confirmed !== true) {
                return;
            }
            this.loading = true;
            this.trackingService.click('favorite_unpublish', this.favorite.id);
            this.adService.unpublishFavorite(this.favorite.id).subscribe(() => {
                this.loading = false;
                // L'annonce reste liée au favori (republication possible), elle n'est plus active
                this.favorite.ad.is_active = false;
                this.alertService.success('Votre bien n\'est plus publié sur LyBox.');
                this.dialogRef.close(this.favorite);
            }, error => {
                this.loading = false;
                this.alertService.error(typeof error === 'string' ? error : 'La dépublication a échoué, veuillez réessayer.');
            });
        });
    }

    onYesClick(): void {
        if (this.loading) {
            return;
        }
        let url = (this.url || '').trim();
        if (url !== '' && !/^https?:\/\//i.test(url)) {
            url = 'https://' + url;
        }
        if (this.offMarket && !this.offMarketEndDate) {
            this.error = 'Veuillez indiquer jusqu\'à quelle date le bien reste off-market.';
            return;
        }
        if (this.offMarket && this.offMarketEndDate < this.minDate) {
            this.error = 'La date de fin d\'off-market doit être postérieure à aujourd\'hui.';
            return;
        }

        this.error = '';
        this.loading = true;
        this.trackingService.click('favorite_publish_confirm', this.favorite.id);
        this.adService.publishFavorite(this.favorite.id, {
            url,
            offmarket: this.offMarket,
            offmarket_end_date: this.offMarket ? this.offMarketEndDate : null,
        }).subscribe(result => {
            this.loading = false;
            // L'API renvoie le favori complet (ad_id + ad publiée) ; repli sur l'état saisi sinon
            const published = result && result.ad && result.ad.user_favorite ? result : this.favorite;
            if (published === this.favorite) {
                published.ad.user_favorite = Object.assign({} as AdUserFavorite, published.ad.user_favorite || {}, {
                    url,
                    offmarket: this.offMarket,
                    offmarket_end_date: this.offMarket ? this.offMarketEndDate : null,
                });
                published.ad.first_provider = 'user';
                published.ad.is_active = true;
            }
            this.alertService.success('Votre bien est publié sur LyBox.');
            this.dialogRef.close(published);
        }, error => {
            this.loading = false;
            this.alertService.error(typeof error === 'string' ? error : 'La publication a échoué, veuillez réessayer.');
        });
    }
}
